import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveAdminAccess } from '../features/admin/adminAccess';

/**
 * Route guard for the administration area.
 *
 * Composes with the app's existing authentication posture rather than adding a
 * second pattern: same loading placeholder, same redirect-to-login for a
 * signed-out visitor. It adds one rule — a signed-in non-administrator is sent
 * to the application home.
 *
 * The decision itself lives in `features/admin/adminAccess` as a pure function
 * so it can be tested without a React test runner.
 *
 * This is NOT a security boundary. It prevents an accidental encounter, not a
 * determined one. Every administration operation is authorized server-side by
 * `isGlobalAdmin`, independently of anything decided here.
 */
export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, isAdmin, mustChangePassword, mfaRequired, mfaEnrolled } = useAuth();

  const decision = resolveAdminAccess({
    loading,
    signedIn: !!user,
    isAdmin,
  });

  // Must come first. Rendering children and redirecting afterwards would have
  // already shown the thing the gate exists to hide.
  if (decision === 'wait') {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (decision === 'login') {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (mfaRequired && !mfaEnrolled) {
    return <Navigate to="/setup-mfa" replace />;
  }

  // `replace` so the administration address does not enter history — a back
  // button should not bounce a non-administrator through the redirect again.
  // No explanatory message: a non-administrator is not told the area exists.
  if (decision === 'home') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
